import { useState, useCallback } from 'react';
import {
  registerUser,
  confirmUserSignup,
  resendVerificationCode,
  loginUser,
  requestPasswordReset,
  completePasswordReset,
  type AuthError,
} from '@/services/auth';

export type AuthFlowState =
  | 'idle'
  | 'submitting'
  | 'success'
  | 'error';

export type AuthFlowStep =
  | 'signup'
  | 'verify'
  | 'login'
  | 'forgotPassword'
  | 'resetPassword';

interface UseAuthFlowState {
  state: AuthFlowState;
  error: AuthError | null;
  pendingEmail: string | null;
}

// Session storage key for persisting pending email across navigation
const PENDING_EMAIL_KEY = 'sunsquatcher-pending-email';

// Helper to get pending email from session storage
function getStoredPendingEmail(): string | null {
  try {
    return sessionStorage.getItem(PENDING_EMAIL_KEY);
  } catch {
    return null;
  }
}

// Helper to set pending email in session storage
function setStoredPendingEmail(email: string | null): void {
  try {
    if (email) {
      sessionStorage.setItem(PENDING_EMAIL_KEY, email);
    } else {
      sessionStorage.removeItem(PENDING_EMAIL_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

export function useAuthFlow() {
  const [flowState, setFlowState] = useState<UseAuthFlowState>(() => ({
    state: 'idle',
    error: null,
    pendingEmail: getStoredPendingEmail(),
  }));

  const clearError = useCallback(() => {
    setFlowState(prev => ({ ...prev, error: null, state: 'idle' }));
  }, []);

  const setPendingEmail = useCallback((email: string | null) => {
    setStoredPendingEmail(email);
    setFlowState(prev => ({ ...prev, pendingEmail: email }));
  }, []);

  /**
   * Register a new user
   * On success, stores email for verification step
   */
  const signup = useCallback(async (email: string, password: string) => {
    setStoredPendingEmail(email);
    setFlowState({ state: 'submitting', error: null, pendingEmail: email });

    const result = await registerUser(email, password);

    if (result.success) {
      setFlowState({ state: 'success', error: null, pendingEmail: email });
      return { success: true, needsVerification: true };
    } else {
      setFlowState({ state: 'error', error: result.error!, pendingEmail: email });
      return { success: false, error: result.error };
    }
  }, []);

  /**
   * Confirm signup with verification code
   */
  const confirmSignup = useCallback(async (email: string, code: string) => {
    setFlowState(prev => ({ ...prev, state: 'submitting', error: null }));

    const result = await confirmUserSignup(email, code);

    if (result.success) {
      // Clear pending email from storage on successful verification
      setStoredPendingEmail(null);
      setFlowState({ state: 'success', error: null, pendingEmail: null });
      return { success: true };
    } else {
      setFlowState(prev => ({ ...prev, state: 'error', error: result.error! }));
      return { success: false, error: result.error };
    }
  }, []);

  /**
   * Resend verification code
   */
  const resendCode = useCallback(async (email: string) => {
    setFlowState(prev => ({ ...prev, state: 'submitting', error: null }));

    const result = await resendVerificationCode(email);

    if (result.success) {
      setFlowState(prev => ({ ...prev, state: 'idle', error: null }));
      return { success: true };
    } else {
      setFlowState(prev => ({ ...prev, state: 'error', error: result.error! }));
      return { success: false, error: result.error };
    }
  }, []);

  /**
   * Sign in with email and password
   */
  const login = useCallback(async (email: string, password: string) => {
    setStoredPendingEmail(null);
    setFlowState({ state: 'submitting', error: null, pendingEmail: null });

    const result = await loginUser(email, password);

    if (result.success) {
      setFlowState({ state: 'success', error: null, pendingEmail: null });
      return { success: true };
    } else {
      // Check if user needs to verify email
      if (result.error?.code === 'UserNotConfirmedException') {
        setStoredPendingEmail(email);
        setFlowState({ state: 'error', error: result.error, pendingEmail: email });
        return { success: false, error: result.error, needsVerification: true };
      }
      setFlowState({ state: 'error', error: result.error!, pendingEmail: null });
      return { success: false, error: result.error };
    }
  }, []);

  /**
   * Request password reset
   */
  const forgotPassword = useCallback(async (email: string) => {
    setStoredPendingEmail(email);
    setFlowState({ state: 'submitting', error: null, pendingEmail: email });

    const result = await requestPasswordReset(email);

    if (result.success) {
      setFlowState({ state: 'success', error: null, pendingEmail: email });
      return { success: true };
    } else {
      setFlowState({ state: 'error', error: result.error!, pendingEmail: email });
      return { success: false, error: result.error };
    }
  }, []);

  /**
   * Complete password reset with code and new password
   */
  const resetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    setFlowState(prev => ({ ...prev, state: 'submitting', error: null }));

    const result = await completePasswordReset(email, code, newPassword);

    if (result.success) {
      // Clear pending email from storage on successful reset
      setStoredPendingEmail(null);
      setFlowState({ state: 'success', error: null, pendingEmail: null });
      return { success: true };
    } else {
      setFlowState(prev => ({ ...prev, state: 'error', error: result.error! }));
      return { success: false, error: result.error };
    }
  }, []);

  return {
    // State
    isSubmitting: flowState.state === 'submitting',
    isSuccess: flowState.state === 'success',
    isError: flowState.state === 'error',
    error: flowState.error,
    pendingEmail: flowState.pendingEmail,

    // Actions
    signup,
    confirmSignup,
    resendCode,
    login,
    forgotPassword,
    resetPassword,
    clearError,
    setPendingEmail,
  };
}
